/**
 * The custom "after admission" address in Login Limit settings.
 * Mirrors `normalizeAdmitUrl()` in `backend/src/services/login-limit-settings.ts`;
 * `test-fixtures/login-limit-admit-url-cases.json` holds both to the same answers.
 */

const MAX_URL_LENGTH = 2048;
const HOST_LABEL_RE = /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?$/;

export type AdmitUrlProblemKey =
  | 'login-limit-admit-url-invalid'
  | 'login-limit-admit-url-https'
  | 'login-limit-admit-url-credentials'
  | 'login-limit-admit-url-mediabriz'
  | 'login-limit-admit-url-host-invalid';

/**
 * A DNS name of two or more labels. `URL` has already lowercased it and
 * turned an international name into punycode, so a single word, an IP
 * address, an empty label, `_`, `*` and other symbols are what fail here.
 */
export function isDomainName(hostname: string): boolean {
  const host = hostname.endsWith('.') ? hostname.slice(0, -1) : hostname;
  const labels = host.split('.');
  return host.length <= 253
    && labels.length >= 2
    && labels.every((label) => HOST_LABEL_RE.test(label))
    && !/^\d+$/.test(labels[labels.length - 1]);
}

/**
 * Null when the address can be saved, otherwise the catalog key for the one reason why not,
 * checked in this order: not an address (empty, too long, spaces, unreadable), not https,
 * a user name or password, a Mediabriz host, then a host that is not a real domain name.
 */
export function admitUrlProblem(value: string): AdmitUrlProblemKey | null {
  const text = value.trim();
  if (!text || text.length > MAX_URL_LENGTH || /\s/.test(text)) return 'login-limit-admit-url-invalid';
  let url: URL;
  try {
    url = new URL(text);
  } catch {
    return /^[a-z][a-z0-9+.-]*:/i.test(text) ? 'login-limit-admit-url-invalid' : 'login-limit-admit-url-https';
  }
  if (url.protocol !== 'https:') return 'login-limit-admit-url-https';
  const authority = text.slice(text.indexOf('//') + 2).split(/[/?#]/)[0];
  if (url.username || url.password || authority.includes('@')) return 'login-limit-admit-url-credentials';
  const host = url.hostname.replace(/\.$/, '');
  if (host === 'mediabriz.com' || host.endsWith('.mediabriz.com')) return 'login-limit-admit-url-mediabriz';
  return host && isDomainName(url.hostname) ? null : 'login-limit-admit-url-host-invalid';
}
